import React, { useRef } from 'react';
import { useCarNameLabel } from '../../../../utils/carNames';
import CallLink from '../../../common/CallLink';
import PageLoader from '../../../common/PageLoader';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import 'bootstrap/dist/css/bootstrap.css';
import { DETAIL_LIST } from '../../../../Redux/Actions/ActionType';
import '../../../../css/style.css';
import moment from 'moment';
import { saveAItem, removeSavedItem, getSavedItems, addToRecentlyViewed } from '../../../../Redux/Actions/UserAction';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import MapModalForView from '../../../common/Modal/MapModalForView';
import { findSavedItemId } from '../../../../utils/savedItems';

const config = {
  headers: {
    'Content-Type': 'application/json',
    Authorization: sessionStorage.getItem('loged_In_auth'),
  },
};

// Fallback images in case API doesn't return any
const defaultImages = [
  {
    original: 'assets/images/user/car-dealers/a.jpg',
    thumbnail: 'assets/images/user/car-dealers/a.jpg',
  },
];

export default function CarDealersDetailsIn() {
  // [CF] Lets a tap on the banner open the gallery full-screen.
  const galleryRef = useRef(null);
  const { t, i18n } = useTranslation();
  const home = useSelector((state) => state.home);
  const allCars = home?.getDetailList || [];
  
  // Get current language for RTL support
  const isRTL = i18n.language === 'ar';
  /* [CF 2026-08-17 #11.1] The model is free text with no Arabic on the
     row; look it up from the model master. See utils/carNames.js */
  const nameLabel = useCarNameLabel(isRTL);
  
  // Function to detect Arabic text content
  const hasArabicText = (text) => {
    if (!text) return false;
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(text);
  };
  
  // Function to determine text alignment based on content
  const getTextAlign = (text) => {
    return isRTL ? 'right' : 'left';
  };

  // Pick the label for the active UI language (English in English mode).
  const L = (en, ar) => (isRTL ? ar || en : en || ar);

  // State to manage the single car's details
  const [carDetails, setCarDetails] = useState(null);
  const savedItemsList = useSelector((state) => state?.home?.savedItemsList);
  // Fill the heart when the car is already saved (or once the user saves it).
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (carDetails) setSaved(!!carDetails.is_saved);
  }, [carDetails?.carId, carDetails?.is_saved]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  // [CF-9] Dealer location (gps now returned with the car) for the Map button.
  const gpsData =
    typeof carDetails?.gps === 'string'
      ? (() => {
          try {
            return JSON.parse(carDetails.gps);
          } catch {
            return {};
          }
        })()
      : carDetails?.gps || {};
  const mapLat = parseFloat(gpsData?.lat);
  const mapLng = parseFloat(gpsData?.long);

  const dispatch = useDispatch();
  const { carId } = useParams();

  useEffect(() => {
    dispatch(getSavedItems());
  }, [dispatch]);

  useEffect(() => {
    const fetchCarDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `https://api.carsavenue.com/api/carDealer/car?carId=${carId}`,
          config
        );

        console.log('API Response:', response);

        if (
          response.data &&
          response.data.response &&
          response.data.response.data
        ) {
          // Store all cars in Redux
          dispatch({
            type: DETAIL_LIST,
            payload: response.data.response.data,
          });

          // Find the specific car matching the carId
          // If the API doesn't filter by carId properly, we'll filter here
          const cars = response.data.response.data;
          const targetCar = cars.find((car) => car.carId === carId);

          if (targetCar) {
            setCarDetails(targetCar);
          } else if (cars.length > 0) {
            // Fallback: use the first car if no match found
            setCarDetails(cars[0]);
            console.warn('Car with ID not found, showing first car instead');
          } else {
            setError('Car not found');
          }
        } else {
          console.error('Unexpected API response structure:', response.data);
          setError('Unexpected API response structure');
        }
      } catch (err) {
        console.error('API Error:', err);
        setError(err.message || 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };
    if (carId && carId !== '') {
      fetchCarDetails();
      // Track this car as recently viewed
      dispatch(addToRecentlyViewed({ carId: carId, type: 'CAR03' }));
    }
  }, [carId, dispatch, setCarDetails]);

  // Effect to filter the car from Redux when it updates
  useEffect(() => {
    if (allCars && allCars.length > 0 && carId) {
      const targetCar = allCars.find((car) => car.carId === carId);
      if (targetCar) {
        setCarDetails(targetCar);
        setLoading(false);
      }
    }
  }, [allCars, carId]);

  /* [CF 2026-08-17 V5] Fall back to the DEALER's contact details.
     A car carries its own phone/WhatsApp columns, but the owner rarely fills
     them: of 283 dealer cars only 76 have a number of their own. For the other
     207 this page built `tel:` out of an empty value and rendered the literal
     "Call#" the client photographed, with WhatsApp opening nothing.
     The car response nests a `carDealer`, but only its id, gps and location —
     no contact — so the dealer has to be fetched once by id. Done here rather
     than in the API so this batch stays frontend-only. */
  const [dealerContact, setDealerContact] = useState(null);
  const dealerIdForContact =
    carDetails?.carDealer?.carDealerId || carDetails?.carDealerId || null;

  useEffect(() => {
    let cancelled = false;
    const carHasOwnNumber = !!String(carDetails?.mobileNumber || '').trim();
    if (!dealerIdForContact || carHasOwnNumber) {
      setDealerContact(null);
      return undefined;
    }
    axios
      .get(
        `https://api.carsavenue.com/api/carDealer/listing?carDealerId=${dealerIdForContact}`,
        config
      )
      .then((res) => {
        if (cancelled) return;
        setDealerContact(res?.data?.response?.data?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) setDealerContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerIdForContact, carDetails?.mobileNumber]);

  /* What the Call / WhatsApp buttons actually use: the car's own numbers when
     the owner set them, otherwise the dealer's. */
  const contactRecord = String(carDetails?.mobileNumber || '').trim()
    ? carDetails
    : dealerContact || carDetails;

  console.log('Car Details:', carDetails);

  // If loading, show spinner
  if (loading) {
    return (
      <div
        className="text-center text-warning"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <PageLoader /> <br />
        {/* <p>Loading details...</p> */}
      </div>
    );
  }

  // If error, show error
  if (error) {
    return (
      <div
        className="text-center text-danger"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <h4>Error loading details</h4>
        <p>{error}</p>
        <Link to="/user-car-dealers" className="btn btn-primary mt-3">
          Back to Car Dealers
        </Link>
      </div>
    );
  }

  // If no car details found after loading
  if (!carDetails) {
    return (
      <div
        className="text-center"
        style={{
          padding: '50px',
        }}
      >
        <h4>No details found</h4>
        <p>The requested car details could not be found.</p>
        <Link to="/user-car-dealers" className="btn btn-primary">
          Back to Car Dealers
        </Link>
      </div>
    );
  }

  // Process images for the gallery
  let galleryImages = defaultImages;

  if (carDetails?.carMedia && carDetails.carMedia.length > 0) {
    const mediaImages = carDetails.carMedia
      .filter(
        (mediaItem) =>
          // Match all saved car-image type spellings (current CAR_IMAGE +
          // legacy CAR-IMAGES/IMAGES). The old strict check matched nothing,
          // so the gallery fell back to hardcoded sample cars.
          mediaItem?.media?.path &&
          [
            'CAR_IMAGE',
            'CAR_IMAGES',
            'CAR-IMAGES',
            'CAR-IMAGE',
            'IMAGES',
          ].includes(mediaItem.mediaType)
      )
      .map((mediaItem) => ({
        original: mediaItem.media.path,
        thumbnail: mediaItem.media.path,
      }));

    if (mediaImages.length > 0) {
      galleryImages = mediaImages;
    }
  }

  //POST TIME
  const formatPostedDate = (dateString) => {
    const date = moment(dateString);
    const now = moment();

    if (date.isSame(now, 'day')) {
      return 'Posted Today';
    } else if (date.isSame(now.clone().subtract(1, 'days'), 'day')) {
      return 'Posted Yesterday';
    } else {
      // [CF] Client: show the DATE only, no time.
      return `Posted on ${date.format('MMM D, YYYY')}`;
    }
  };

  const onHandleSave = () => {
    // Check if user is logged in
    const isLoggedIn = sessionStorage.getItem('loged_In') === 'true';
    
    if (!isLoggedIn) {
      Swal.fire({
        title: 'Please Login to Save',
        text: 'You need to be logged in to save this car. Please login first.',
        icon: 'warning',
        imageUrl: './assets/images/favicon/android-chrome-192x192.png',
        imageHeight: 100,
        confirmButtonColor: '#3ab1f7',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Login',
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          // Redirect to login page
          window.location.href = '#/login';
        }
      });
      return;
    }

    // User is logged in, proceed with save (optimistically fill the heart).
    setSaved(true);
    dispatch(
      saveAItem({
        carId: carDetails?.carId,
      })
    );
  };

  // Share = copy the page link (or native share sheet). Users paste it into
  // whatever app they like — no in-app social modal.
  const handleShareClick = (e) => {
    if (e) e.preventDefault();
    const url = window.location.href;
    const title =
      L(carDetails?.brand?.name, carDetails?.brand?.nameAr) ||
      carDetails?.carName ||
      'CarsAvenue';
    if (navigator.share) {
      navigator.share({ title, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() =>
          Swal.fire({ title: 'Link copied to clipboard', icon: 'success' })
        )
        .catch(() =>
          Swal.fire({ title: 'Copy this link', text: url, icon: 'info' })
        );
    } else {
      Swal.fire({ title: 'Copy this link', text: url, icon: 'info' });
    }
  };

  // Specifications block — reused in two spots so the section order differs by
  // screen size (client request #6): on desktop it sits under the gallery in the
  // left column; on mobile it must come LAST (after Vehicle Details + Description),
  // so the in-column copy is hidden on mobile and a mobile-only copy is rendered
  // at the very end.
  const specificationsBlock = (
    <>
      <h5 className="cav-section-title">{t('Specifications')}</h5>
      <table
        className="table table-bordered table-striped"
        style={{ textAlign: isRTL ? 'right' : 'left' }}
      >
        <tbody>
          <tr>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{t('Interior Color')}</td>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{L(carDetails?.interiorColor?.color, carDetails?.interiorColor?.colorAr) || 'N/A'}</td>
          </tr>
          <tr>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{t('Seats')}</td>
            <td style={{ textAlign: getTextAlign(carDetails?.seats?.seat) }}>{carDetails?.seats?.seat || 'N/A'}</td>
          </tr>
          <tr>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{t('Fuel Type')}</td>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{L(carDetails?.fuelType?.fuelType, carDetails?.fuelType?.fuelTypeAr) || 'N/A'}</td>
          </tr>
          <tr>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{t('Door')}</td>
            <td style={{ textAlign: getTextAlign(carDetails?.doors?.door) }}>{carDetails?.doors?.door || 'N/A'}</td>
          </tr>
          <tr>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{t('Comforts')}</td>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>
              {(carDetails?.comfortId || [])
                .map((comfort) => L(comfort?.comfort, comfort?.comfortAr))
                .filter(Boolean)
                .join(', ') || 'N/A'}
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>{t('Safety')}</td>
            <td style={{ textAlign: isRTL ? 'right' : 'left' }}>
              {(carDetails?.safetyId || [])
                .map((safety) => L(safety?.safety, safety?.safetyAr))
                .filter(Boolean)
                .join(', ') || 'N/A'}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );

  // If we have data, render it
  return (
    <div className="main-content-area">
      <div className="container">
        {/* [CF] mb-5 (48px) left a large empty band before the
                Specifications section — trimmed to mb-3. */}
          <div className="row mb-3">
          <div className="col-12 col-sm-12 col-md-6 col-lg-7 mb-2 mb-sm-2 mb-md-2 mb-lg-0">
            <div className="bxslider cav-car-gallery">
              <ImageGallery
                        ref={galleryRef}
                items={galleryImages}
                className="img-fluid"
                infinite={true}
                showNav={false}
                showFullscreenButton={false}
                        // [CF] Client: tapping the banner opens the full view.
                        onClick={() => galleryRef.current?.fullScreen()}
                        useBrowserFullscreen={false}
                showPlayButton={false}
                showBullets={true}
                slideInterval={3000}
              />
            </div>

            {/* Specifications under the gallery on desktop; hidden on mobile
                (a mobile-only copy is rendered last — see below). */}
            <div className="d-none d-md-block">{specificationsBlock}</div>
          </div>

          <div className="col-12 col-sm-12 col-md-6 col-lg-5 flex align-items-start ">
            <div
              className="sidebar-box"
              id="sticky-box"
              style={{ position: 'sticky', top: '16px', alignSelf: 'flex-start' }}
            >
              {/* [CF] Posted date and Save/Share share ONE row (client: move
                  share and save up). Matches the car-for-sale detail page. */}
              <div
                className="mb-2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  // [CF 2026-08-02] Save/Share must sit on the LEFT and the posted date
                  // on the RIGHT in BOTH languages. `space-between` alone mirrors with
                  // the page, so in Arabic they swapped sides; row-reverse in RTL flips
                  // the main axis back so the physical placement is language-independent.
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <div
                  className="save-share"
                  style={{ display: 'flex', alignItems: 'center', gap: '18px', flexShrink: 0 }}
                >
                  <span onClick={onHandleSave} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <i
                      className={saved ? 'fas fa-heart' : 'far fa-heart'}
                      style={{ color: saved ? '#FEBA00' : 'inherit' }}
                    ></i>{' '}
                    {t('Save')}
                  </span>
                  <span onClick={handleShareClick} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <i className="far fa-share-square"></i> {t('Share')}
                  </span>
                </div>
                <p className="mb-0" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                  <small>
                    <i className="far fa-calendar-alt"></i>{' '}
                    {carDetails?.createdAt &&
                      formatPostedDate(carDetails.createdAt)}
                  </small>
                </p>
              </div>

              {/* [CF] Brand and model on ONE line, so the price takes the line
                  the model used to occupy. */}
              <div
                className="mb-2"
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <h3 className="mb-0" style={{ textAlign: getTextAlign(carDetails?.carName) }}>
                  {L(carDetails?.brand?.name, carDetails?.brand?.nameAr) || carDetails?.carName || ''}
                </h3>
                <p className="mb-0" style={{ textAlign: getTextAlign(carDetails?.carModel) }}>
                  {nameLabel(carDetails?.carModel) || ''}
                </p>
              </div>
              {carDetails?.category ? (
                <p className="mb-2" style={{ textAlign: getTextAlign(carDetails?.category) }}>
                  {carDetails.category}
                </p>
              ) : null}

              <h4 className="mb-4" style={{ color: '#FEBA00', textAlign: getTextAlign(carDetails?.price) }}>
                KD {carDetails?.price || ''}
              </h4>

              {/* #17 — original-HTML horizontal stat bar (dark rounded pill):
                  Year | Color | Km | Cylinder. DOM order matches the original
                  template and renders right-to-left correctly in Arabic. */}
              <div className="car-details-block">
                <div className="row">
                  <div className="col-3">
                    <div className="line-a">{t('Year')}</div>
                    <div className="line-b">
                      {carDetails?.modelYear?.modelYear || 'N/A'}
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="line-a">{t('Color')}</div>
                    <div className="line-b">
                      {L(carDetails?.exteriorColor?.color, carDetails?.exteriorColor?.colorAr) || 'N/A'}
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="line-a">{t('Km')}</div>
                    <div className="line-b">
                      {carDetails?.kilometerDriven || 'N/A'}
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="line-a">{t('Cylinder')}</div>
                    <div className="line-b">
                      {carDetails?.cyclinder?.cyclinder || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="car-agent-cwm" style={{ marginTop: '20px' }}>
                <div className="row">
                  <div className="col-4">
                    {/* [CF 2026-08-17 V5] Was `tel:${carDetails?.mobileNumber || '#'}`
                        — most cars carry no number of their own, so this became a
                        link to "#" and rendered as "Call#". Now uses the car's
                        number when there is one and the dealer's otherwise, and
                        offers all of them when there is more than one. */}
                    <CallLink
                      record={contactRecord}
                      className="btn btn-outline-secondary btn-block"
                      style={{ padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <img
                        src="assets/images/icons/user/car-dealers/telephone.svg"
                        alt=""
                        className="icon-cwm"
                        style={{ width: '20px', height: '20px' }}
                      />
                      <span>{t('Call')}</span>
                    </CallLink>
                  </div>

                  <div className="col-4">
                    {/* [CF 2026-08-17 V5] Same fallback as Call. This used only
                        the car's own WhatsApp number, which is usually empty, so
                        the link resolved to a bare "wa.me/" and opened nothing.
                        Rendered inert when neither the car nor the dealer has a
                        WhatsApp number, rather than as a dead link. */}
                    {(() => {
                      const wa = String(
                        contactRecord?.whatsAppNumber || ''
                      ).replace(/\D/g, '');
                      const code = String(
                        contactRecord?.whatsAppNumberCountryCode || ''
                      ).replace(/\D/g, '');
                      const full = wa && code && !wa.startsWith(code) ? code + wa : wa;
                      const shared = {
                        className: 'btn btn-outline-secondary btn-block',
                        style: { padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
                      };
                      const inner = (
                        <>
                          <img
                            src="assets/images/icons/user/car-dealers/whatsapp.svg"
                            alt=""
                            className="icon-cwm"
                            style={{ width: '20px', height: '20px' }}
                          />
                          <span>{t('WhatsApp')}</span>
                        </>
                      );
                      return full ? (
                        <a
                          {...shared}
                          target="_blank"
                          rel="noreferrer"
                          href={`https://wa.me/${full}`}
                        >
                          {inner}
                        </a>
                      ) : (
                        <a
                          {...shared}
                          href="#!"
                          aria-disabled="true"
                          onClick={(e) => e.preventDefault()}
                          style={{ ...shared.style, opacity: 0.45, cursor: 'default' }}
                        >
                          {inner}
                        </a>
                      );
                    })()}
                  </div>

                  <div className="col-4">
                    <button
                      className="btn btn-outline-secondary btn-block"
                      onClick={() => setMapModalOpen(true)}
                      style={{ padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <img
                        src="assets/images/icons/user/car-dealers/map.svg"
                        alt="Map"
                        className="icon-cwm"
                        style={{ width: '20px', height: '20px' }}
                      />
                      <span>{t('Map')}</span>
                    </button>
                    <MapModalForView
                      open={mapModalOpen}
                      handleClose={() => setMapModalOpen(false)}
                      currentLocation={{ lat: mapLat, long: mapLng }}
                    />
                  </div>
                </div>
              </div>

              <div className="cav-car-desc">
                {(
                  carDetails?.description ||
                  'No description available for this vehicle.'
                )
                  .split(/\r?\n|(?=✅)|(?=✔️)|(?=✈️)|(?=•)/)
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((line, idx) => (
                    <div className="cav-car-desc-line" key={idx}>
                      {line}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile-only Specifications — rendered LAST so the mobile order is
            Gallery → Vehicle Details → Description → Specifications (#6). */}
        <div className="row d-md-none">
          <div className="col-12">{specificationsBlock}</div>
        </div>

        <div className="row">
          <div className="col-12 col-sm-12 col-md-6 col-lg-7">
            {/* Specifications relocated under the gallery above. */}

            {/* <h4>Comfort and Convenience</h4>

            <table className="table table-bordered table-striped table-sm mb-0">
              <tbody>
                {carDetails?.features?.map((feature, index) => (
                  <tr key={index}>
                    <td>&raquo; {feature}</td>
                  </tr>
                )) || (
                  <>
                    <tr>
                      <td>&raquo; Full Options</td>
                    </tr>
                    <tr>
                      <td>&raquo; Sunroof</td>
                    </tr>
                    <tr>
                      <td>&raquo; Sensors</td>
                    </tr>
                    <tr>
                      <td>&raquo; Backup Cameras</td>
                    </tr>
                    <tr>
                      <td>&raquo; Keyless Entry</td>
                    </tr>
                    <tr>
                      <td>&raquo; Navigation</td>
                    </tr>
                    <tr>
                      <td>&raquo; Leather Seats</td>
                    </tr>
                    <tr>
                      <td>&raquo; Cruise Control</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table> */}
          </div>
        </div>
      </div>
    </div>
  );
}
